import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'

import api from 'api'
import { digObject } from 'functions/utilities'
import { addAction } from 'functions/undoRedo'
import generateID from 'functions/generateID'

const CREATE_REQUEST = 'realhub/templatePageItem/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/templatePageItem/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/templatePageItem/CREATE_FAILURE'

const UPDATE_REQUEST = 'realhub/templatePageItem/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/templatePageItem/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/templatePageItem/UPDATE_FAILURE'

const DELETE_REQUEST = 'realhub/templatePageItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/templatePageItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/templatePageItem/DELETE_FAILURE'

const SELECT_PAGE_ITEM = 'realhub/templatePageItem/SELECT_ITEM'

const UPDATE_STATE = 'realhub/templatePageItem/UPDATE_STATE'

// Initial State
const initialState = {
  creating: false,
  deleting: false,
  errors: [],
  result: [],
  selected: {},
  selectedId: null,
  undoRedoActions: {
    undo: [],
    redo: [],
  },
  updating: false,
}

// Actions
export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(payload = {}, options = {}){
  return {
    type: CREATE_SUCCESS,
    options,
    result: payload.result,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(payload = {}){
  return {
    type: UPDATE_SUCCESS,
    result: payload.result,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(payload = {}, options = {}){
  return {
    type: DELETE_SUCCESS,
    options,
    result: payload.result,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

export function selectTemplatePageItem(templatePageItem){
  return (dispatch) => {
    dispatch({
      type: SELECT_PAGE_ITEM,
      selectedId: templatePageItem.id,
      selected: templatePageItem.id ? { ...templatePageItem } : {},
    })
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

const baseUndoRedoParams = {
  reducer: 'templatePageItems',
  updateStateType: UPDATE_STATE,
}

// Action Creators
export function hydrateTemplatePageItem(templatePageItem){
  const normalizedJson = normalize(templatePageItem, Schemas.TEMPLATE_PAGE_ITEM)

  return (dispatch) => {
    dispatch(updateEntities(normalizedJson))
  }
}

export function createTemplatePageItem(templatePageItem, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      template_page_item: templatePageItem,
    }),
  }

  return (dispatch, getState) => {
    // Set Loading
    dispatch(createRequest())

    const promise = api('/admin/template_page_items.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.TEMPLATE_PAGE_ITEM)
        dispatch(updateEntities(normalizedJson))

        // Undo/Redo Logic
        const undoRedoKey = digObject(options, 'undoRedoKey', 'undo')

        addAction(
          undoRedoKey,
          {
            ...baseUndoRedoParams,
            id: generateID(),
            key: undoRedoKey,
            redo: {
              action: 'createTemplatePageItem',
              params: [
                { key: 'item', value: templatePageItem },
                { key: 'options', value: options },
                { key: 'callback', value: callback },
              ],
            },
            undo: {
              action: 'deleteTemplatePageItem',
              params: [
                { key: 'item', value: json },
                { key: 'options', value: options },
                { key: 'callback', value: callback },
              ],
            },
          },
          { dispatch, getState },
        )

        dispatch(createSuccess())

        if (callback) callback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function updateTemplatePageItem(templatePageItem, options = {}, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      template_page_item: templatePageItem,
    }),
  }

  return (dispatch, getState) => {
    // Get Item before Change
    const { templatePageItems } = getState().entities
    const itemBeforeChange = templatePageItems[templatePageItem.id] || {}

    const previousItemParams = Object.keys(templatePageItem).reduce((acc, key) => {
      acc[key] = itemBeforeChange[key]
      return acc
    }, {})

    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/template_page_items/${templatePageItem.id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.TEMPLATE_PAGE_ITEM)
        dispatch(updateEntities(normalizedJson))

        // Undo/Redo Logic
        const undoRedoKey = digObject(options, 'undoRedoKey', 'undo')

        addAction(
          undoRedoKey,
          {
            ...baseUndoRedoParams,
            id: generateID(),
            key: undoRedoKey,
            redo: {
              action: 'updateTemplatePageItem',
              params: [
                { key: 'item', value: templatePageItem },
                { key: 'options', value: options },
                { key: 'callback', value: callback },
              ],
            },
            undo: {
              action: 'updateTemplatePageItem',
              params: [
                { key: 'item', value: previousItemParams },
                { key: 'options', value: options },
                { key: 'callback', value: callback },
              ],
            },
          },
          { dispatch, getState },
        )

        dispatch(updateSuccess())

        if (callback) callback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function duplicateTemplatePageItem(templatePageItem, options = {}, callback){
  const config = {
    method: 'POST',
  }

  return (dispatch, getState) => {
    // Set Loading
    dispatch(createRequest())

    const promise = api(`/admin/template_page_items/${templatePageItem.id}/duplicate.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.TEMPLATE_PAGE_ITEM)
        dispatch(updateEntities(normalizedJson))

        // Undo/Redo Logic
        const undoRedoKey = digObject(options, 'undoRedoKey', 'undo')

        addAction(
          undoRedoKey,
          {
            ...baseUndoRedoParams,
            id: generateID(),
            key: undoRedoKey,
            redo: {
              action: 'duplicateTemplatePageItem',
              params: [
                { key: 'item', value: templatePageItem },
                { key: 'options', value: options },
                { key: 'callback', value: callback },
              ],
            },
            undo: {
              action: 'deleteTemplatePageItem',
              params: [
                { key: 'item', value: json },
                { key: 'options', value: options },
                { key: 'callback', value: callback },
              ],
            },
          },
          { dispatch, getState },
        )

        dispatch(createSuccess())

        if (callback) callback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function deleteTemplatePageItem(templatePageItem, options = {}, callback){
  const config = {
    method: 'DELETE',
  }

  return (dispatch, getState) => {
    // Set Loading
    dispatch(deleteRequest())

    const promise = api(`/admin/template_page_items/${templatePageItem.id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.TEMPLATE_PAGE_ITEM)
        dispatch(deleteEntity(normalizedJson))

        // Undo/Redo Logic
        const undoRedoKey = digObject(options, 'undoRedoKey', 'undo')

        addAction(
          undoRedoKey,
          {
            ...baseUndoRedoParams,
            id: generateID(),
            key: undoRedoKey,
            redo: {
              action: 'deleteTemplatePageItem',
              params: [
                { key: 'item', value: templatePageItem },
                { key: 'options', value: options },
                { key: 'callback', value: callback },
              ],
            },
            undo: {
              action: 'createTemplatePageItem',
              params: [
                { key: 'item', value: templatePageItem },
                { key: 'options', value: options },
                { key: 'callback', value: callback },
              ],
            },
          },
          { dispatch, getState },
        )

        dispatch(deleteSuccess())

        if (callback) callback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case CREATE_REQUEST:
      return { ...state, creating: true }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        result: action.result || state.result,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return { ...state, updating: true }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case DELETE_REQUEST:
      return { ...state, deleting: true }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        result: action.result,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors,
      }
    case SELECT_PAGE_ITEM:
      return {
        ...state,
        selectedId: action.selectedId,
        selected: action.selected,
      }
    case UPDATE_STATE:
      return {
        ...state,
        ...action.payload,
      }
    default:
      return state
  }
}
